import { useTranslation } from "react-i18next";
import { Icon, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@nexus/ui";
import { DoubleDownButton } from "@/features/post-flight/components/DoubleDownButton";
import type { PostFlightOpportunity } from "@/features/post-flight/api/post-flight.api";
import { PostFlightWindowContext } from "@/features/persona-opportunities/components/shared/PostFlightWindowContext";
import { postFlightWindowContextFromListRow } from "@/features/persona-opportunities/lib/post-flight-opportunity-metrics";
import {
  adTitle,
  evidenceLabel,
  formatPercent,
  formatRatePair,
  isEvenRate,
  formatShare,
  formatSpend,
  isImageCreative,
  isRunning,
  ratio,
  toPercentPoints,
  type PostFlightAd,
} from "@/features/post-flight/lib/post-flight-list";

const TYPE_CHIP: Record<string, string> = {
  winning_creative: "bg-success-subtle text-success-subtle-foreground",
  underexplored_winner: "bg-info-subtle text-info-subtle-foreground",
  emerging_creative: "bg-warning-subtle text-warning-subtle-foreground",
  strong_hook: "bg-warning-subtle text-warning-subtle-foreground",
};

const THUMB_CHIP = "inline-flex items-center gap-1 rounded-sm bg-white/90 px-1.5 py-0.5 text-[11px] leading-[15px] text-[#374151]";

interface AdCardProps {
  ad: PostFlightAd;
  listAsOf?: string | null;
  canDoubleDown?: boolean;
  onOpen: () => void;
  onPlay: () => void;
  onDoubleDown: () => void;
}

export function AdCard({ ad, listAsOf = null, canDoubleDown = true, onOpen, onPlay, onDoubleDown }: AdCardProps) {
  const { t } = useTranslation();
  const lead = ad.rows[0];
  const mediaRow = ad.rows.find((row) => row.video_url);
  const mediaUrl = mediaRow?.video_url ?? null;
  const image = isImageCreative(mediaUrl, mediaRow?.object_type);
  const running = isRunning(lead?.active);
  const share = typeof lead?.spend_share === "number" ? formatShare(lead.spend_share) : null;
  const hookRatio = ratio(lead?.thumbstop, lead?.thumbstop_expected);
  const holdRatio = ratio(lead?.hold_rate, lead?.account_hold_rate);
  const hookRate = rateDisplay(
    t,
    "postFlight.card.hookTip",
    "{{value}} stop vs {{baseline}} average. Above the average, more people stop scrolling.",
    "postFlight.card.hookTipBelow",
    "{{value}} stop vs {{baseline}} average. Below the average, fewer people stop scrolling.",
    "postFlight.card.hookTipPlain",
    "{{value}} stop vs {{baseline}} average.",
    lead?.thumbstop,
    lead?.thumbstop_expected,
  );
  const holdRate = rateDisplay(
    t,
    "postFlight.card.holdTip",
    "{{value}} hold vs {{baseline}} average. Above the average, more people keep watching.",
    "postFlight.card.holdTipBelow",
    "{{value}} hold vs {{baseline}} average. Below the average, fewer people keep watching.",
    "postFlight.card.holdTipPlain",
    "{{value}} hold vs {{baseline}} average.",
    lead?.hold_rate,
    lead?.account_hold_rate,
  );

  return (
    <article aria-label={adTitle(ad)} className="flex flex-col overflow-hidden rounded-md border border-border bg-card">
      <button type="button" onClick={onOpen} className="flex flex-col text-left">
        <div
          className="relative aspect-[4/5] overflow-hidden bg-muted"
          onClick={(event) => {
            event.stopPropagation();
            if (image) onOpen();
            else onPlay();
          }}
        >
          {mediaUrl && image ? (
            <img src={mediaUrl} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover" />
          ) : null}
          {mediaUrl && !image ? (
            <video
              src={mediaUrl}
              muted
              playsInline
              preload="metadata"
              className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            />
          ) : null}
          <div className="absolute top-1.5 left-1.5 flex items-center gap-1">
            {mediaUrl ? (
              <span className={THUMB_CHIP}>
                <Icon name={image ? "image" : "clapperboard"} className="size-3 text-[#374151]" />
                {image ? t("postFlight.card.image", "Image") : t("postFlight.card.video", "Video")}
              </span>
            ) : null}
            {running === null ? null : (
              <span className={THUMB_CHIP}>
                {running ? t("postFlight.card.running", "Running") : t("postFlight.card.ended", "Ended")}
              </span>
            )}
          </div>
          {mediaUrl && !image ? (
            <span className="pointer-events-none absolute top-1/2 left-1/2 flex size-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white">
              <Icon name="play" className="size-3.5 text-white" />
            </span>
          ) : null}
          {typeof lead?.days_live === "number" ? (
            <span className={`absolute bottom-1.5 left-1.5 ${THUMB_CHIP}`}>
              <Icon name="calendar" className="size-3 text-[#374151]" />
              {t("postFlight.card.daysActive", { count: lead.days_live })}
            </span>
          ) : null}
        </div>
        <div className="flex flex-col gap-1.5 border-t border-border p-3">
          <h3 className="truncate text-[13px]">{adTitle(ad)}</h3>
          <PostFlightWindowContext
            data={postFlightWindowContextFromListRow(lead, listAsOf)}
            listAsOf={listAsOf}
            fallbackWindowDays={lead?.window_days}
          />
          {ad.rows.some((row) => typeof row.pct_better === "number" && Boolean(row.kpi)) ? (
            <div className="flex flex-col gap-2 rounded-sm border border-border bg-muted p-2.5">
              <p className="text-[11px] tracking-wide text-muted-foreground uppercase">
                {t("postFlight.card.why", "Why it's here")}
              </p>
              {ad.rows.map((row) => (
                <WhyRow key={`${ad.hash}-${row.source}-${row.opportunity_type}`} row={row} />
              ))}
            </div>
          ) : null}
          {typeof lead?.spend === "number" || hookRate || holdRate ? (
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              {typeof lead?.spend === "number" ? (
                <span className="tabular-nums">
                  <span className="text-muted-foreground">{t("postFlight.card.spend", "Spend")}</span>
                  {" "}
                  {formatSpend(lead.spend)}
                  {share ? <span className="text-muted-foreground"> · {share} {t("postFlight.card.ofAccount", "of account")}</span> : null}
                </span>
              ) : null}
              {hookRate && typeof lead?.thumbstop === "number" ? (
                <RateChip
                  label={t("postFlight.card.hook", "Hook")}
                  value={hookRate.value}
                  tip={hookRate.tip}
                  tone={rateTone(hookRate.even, hookRatio)}
                />
              ) : null}
              {holdRate && typeof lead?.hold_rate === "number" ? (
                <RateChip
                  label={t("postFlight.card.hold", "Hold")}
                  value={holdRate.value}
                  tip={holdRate.tip}
                  tone={rateTone(holdRate.even, holdRatio)}
                />
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="flex flex-col gap-3 border-t border-border p-3">
          {ad.rows.some((row) => typeof row.pct_better === "number" && Boolean(row.kpi)) ? null : (
            <p className="text-[11px] tracking-wide text-muted-foreground uppercase">
              {t("postFlight.card.why", "Why it's here")}
            </p>
          )}
          {ad.rows.map((row) => (
            <div key={`${ad.hash}-${row.source}-reason-${row.opportunity_type}`} className="flex flex-col gap-1.5">
              <div className="flex flex-wrap items-center gap-1.5">
                <TypeChip type={row.opportunity_type} label={t(`postFlight.types.${row.opportunity_type}`, row.opportunity_type)} />
                {evidenceLabel(row.evidence) ? (
                  <span className="text-[11px] text-muted-foreground">{evidenceLabel(row.evidence)}</span>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </button>
      <div className="mt-auto px-3 pb-3">
        <DoubleDownButton image={image} className="w-full" canDoubleDown={canDoubleDown} onClick={onDoubleDown} />
      </div>
    </article>
  );
}

function WhyRow({ row }: { row: PostFlightOpportunity }) {
  const { t } = useTranslation();
  const headline = whyHeadline(t, row);
  const detail = whyDetail(t, row);
  return (
    <div className="flex flex-col gap-1">
      <TypeChip type={row.opportunity_type} label={t(`postFlight.types.${row.opportunity_type}`, row.opportunity_type)} />
      {headline ? <p className="text-[13px] font-medium text-success">{headline}</p> : null}
      {detail ? <p className="text-[11px] text-muted-foreground tabular-nums">{detail}</p> : null}
    </div>
  );
}

function RateChip({
  label,
  value,
  tip,
  tone,
}: {
  label: string;
  value: string;
  tip: string;
  tone: "above" | "even" | "below";
}) {
  const toneClass = tone === "above" ? "text-success" : tone === "below" ? "text-destructive" : "text-foreground";
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="group"
            aria-label={tip}
            className={`inline-flex items-center gap-1 rounded-sm border border-border bg-muted px-1.5 py-0.5 ${toneClass}`}
          >
            <span className="text-muted-foreground">{label}</span>
            <span className="tabular-nums">{value}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent>{tip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function rateDisplay(
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
  aboveKey: string,
  aboveFallback: string,
  belowKey: string,
  belowFallback: string,
  plainKey: string,
  plainFallback: string,
  value: number | null | undefined,
  baseline: number | null | undefined,
): { value: string; tip: string; even: boolean } | null {
  if (typeof value !== "number") return null;
  const compared = ratio(value, baseline);
  if (compared === null || typeof baseline !== "number") {
    const shown = formatPercent(value);
    return {
      value: shown,
      tip: t(plainKey, plainFallback, { value: shown, baseline: "—" }),
      even: false,
    };
  }
  const pair = formatRatePair(value, baseline);
  if (isEvenRate(value, baseline)) {
    return {
      value: pair.value,
      tip: t(plainKey, plainFallback, pair),
      even: true,
    };
  }
  const below = compared < 1;
  return {
    value: pair.value,
    tip: t(below ? belowKey : aboveKey, below ? belowFallback : aboveFallback, pair),
    even: false,
  };
}

function rateTone(even: boolean, compared: number | null): "above" | "even" | "below" {
  if (even || compared === null) return "even";
  return compared >= 1 ? "above" : "below";
}

function TypeChip({ type, label }: { type: string; label: string }) {
  return (
    <span className={`inline-flex h-5 items-center gap-1 self-start rounded-sm px-1.5 text-[10px] ${TYPE_CHIP[type] ?? "bg-muted text-foreground"}`}>
      <span className="size-1.5 rounded-full bg-current" />
      {label}
    </span>
  );
}

function whyHeadline(
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
  row: PostFlightOpportunity,
): string | null {
  if (typeof row.pct_better !== "number" || !row.kpi) return null;
  const pct = toPercentPoints(row.pct_better);
  if (row.kpi === "thumbstop") {
    return t("postFlight.card.statThumbstop", "{{pct}}% more people stop scrolling", { pct });
  }
  if (row.kpi === "concept_cpa") {
    return t("postFlight.card.statConceptCpa", "Concept CPA {{pct}}% lower than account", { pct });
  }
  if (row.kpi === "cpa") {
    return t("postFlight.card.statCpa", "CPA {{pct}}% lower than account", { pct });
  }
  return t("postFlight.card.statBetter", "{{pct}}% better than the account", { pct });
}

function whyDetail(
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
  row: PostFlightOpportunity,
): string | null {
  if (typeof row.kpi_value !== "number" || typeof row.baseline_value !== "number") return null;
  if (row.kpi === "thumbstop") {
    const pair = formatRatePair(row.kpi_value, row.baseline_value);
    return t("postFlight.card.statThumbstopDetail", "{{value}} stop rate vs {{baseline}} average", {
      value: pair.value,
      baseline: pair.baseline,
    });
  }
  const value = Math.round(row.kpi_value).toLocaleString("en-US");
  const baseline = Math.round(row.baseline_value).toLocaleString("en-US");
  if (typeof row.purchases === "number") {
    return t("postFlight.card.statCpaDetailPurchases", "{{value}} vs {{baseline}} account · {{count}} purchases", {
      value,
      baseline,
      count: Math.round(row.purchases),
    });
  }
  return t("postFlight.card.statCpaDetail", "{{value}} vs {{baseline}} account", { value, baseline });
}
